import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../services/config.service';
import { formatString } from '../utils/string-utils';

@Injectable({
	providedIn: 'root'
})
export class AlbumsService {

	constructor(
		private configService: ConfigService,
		private http: HttpClient
	) { }

	async getAlbum(id: number = 1, constraints: any = {}): Promise<any> {
		constraints.ids = [id];

		let url = this.configService.getConfig().albums.albumsURL + "?constraints=" + encodeURIComponent(JSON.stringify(constraints));

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				var album = response.content[0];
				this.formatAlbum(album);
				return album;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getAlbums(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().albums.albumsURL;
		url = url + "?page=" + page + "&size=" + paging.elementsPerPage;
		url = url + "&sort=" + paging.sorting.sort + "&constraints=" + encodeURIComponent(JSON.stringify(constraints));

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let albums = response.content;
				albums.forEach((album: any) => {
					this.formatAlbum(album);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getSharedAlbums(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().albums.albumsSharedURL;
		url = url + "?page=" + page + "&size=" + paging.elementsPerPage;
		url = url + "&sort=" + paging.sorting.sort + "&constraints=" + encodeURIComponent(JSON.stringify(constraints));

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let albums = response.content;
				albums.forEach((album: any) => {
					this.formatAlbum(album);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addAlbum(name: string, description: string): Promise<any> {
		let url = this.configService.getConfig().albums.albumsAddURL + "?name=" + name + "&description=" + encodeURIComponent(description);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async deleteAlbum(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsDeleteURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addAlbumProduct(id: number, product: any): Promise<any> {
		let params = new HttpParams();
		for (const key in product) {
			if (product.hasOwnProperty(key) && product[key]) {
				params = params.set(key, product[key].toString());
			}
		}

		let url = formatString(this.configService.getConfig().albums.albumsAddToURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, { params }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async deleteAlbumProduct(id: number, product: any): Promise<any> {
		let params = new HttpParams();
		for (const key in product) {
			if (product.hasOwnProperty(key) && product[key]) {
				params = params.set(key, product[key].toString());
			}
		}

		let url = formatString(this.configService.getConfig().albums.albumsDeleteFromURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, { params }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async deleteAlbumProducts(id: number, prodids: any[]): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsMassDeleteFromURL, id);
		url = url + "?uuids=" + prodids;

		try {
			return firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateAlbumPageMetadata(id: number, uuid: string, page: number, metadataHash: any): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsPageMetadataUpdate, id, uuid, page);
		url += `?metadata=${encodeURIComponent(JSON.stringify(metadataHash))}`;

		try {
			return await firstValueFrom(this.http.post<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getAlbumDescription(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsDescribeURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getMinimalAlbumDescription(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsDescribeMinimalURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getAlbumProductDescription(albumID: number, productID: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsDescribeProductURL, albumID, productID);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async cleanAlbum(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsCleanURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async renameAlbum(id: number, name: string, description: string): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsRenameURL, id);
		url = url + "?name=" + name + "&description=" + encodeURIComponent(description);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async albumslist(useronly: boolean = false): Promise<any> {
		let url = this.configService.getConfig().albums.albumsList + "?owned=" + useronly;

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async addAlbumShare(id: number, users: any = []): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsAddShareURL, id);
		url = url + "?ids=" + users.join(",");

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async removeAlbumShare(id: number, email?: string, user_id?: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsRemoveShareURL, id);
		url = url + (email ? "?email=" + email : "");
		url = url + (user_id ? "?user_id=" + user_id : ("?user_id=" + ''));

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getAlbumsForProduct(id: number, page?: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsForProduct, id);
		url = url + (page ? "?page=" + page : "");

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));
			let albums = response;
			albums.forEach((album: any) => {
				this.formatAlbum(album);
			});
			return response;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async downloadAlbum(id: number): Promise<any> {
		let url = formatString(this.configService.getConfig().albums.albumsDownloadURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	private formatAlbum(album: any) {
		if (!album) return;

		var created = new Date(album.created);
		album.createdStr = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');

		album.modifiedStr = ""
		if (album.modified) {
			var modified = new Date(album.modified)
			album.modifiedStr = modified.toLocaleDateString('en-US') + ' ' + modified.toLocaleTimeString('en-US')
		}

		album._thumbs = formatString(this.configService.getConfig().albums.albumsThumbnailURL, album.id);

		if ("undefined" === album.description) {
			album.description = undefined;
		}

		album.shared = album.public || (album.albumRestrictions ? album.albumRestrictions.length > 0 : false);
	}
}
